import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useCreateSeance, useDeleteSeance, useLieux, useManifestations, useSeances } from '../../api/hooks';
import { formatDate, fromLocalInput, toLocalInput } from '../../lib/format';
import { Button, EmptyState, Field, Input, ROW, Section, Select, Spinner, TABLE, TD, TH, THEAD_ROW } from '../ui';
import { useToast } from '../toast';

export default function SeancesTab() {
  const toast = useToast();
  const { data, isPending } = useSeances();
  const { data: manifestations } = useManifestations();
  const { data: lieux } = useLieux();
  const create = useCreateSeance();
  const remove = useDeleteSeance();

  const [idManif, setIdManif] = useState('');
  const [idLieu, setIdLieu] = useState('');
  const [dateSeance, setDateSeance] = useState(toLocalInput(new Date(Date.now() + 48 * 3600_000).toISOString()));
  const [dateFinSeance, setDateFinSeance] = useState(
    toLocalInput(new Date(Date.now() + 52 * 3600_000).toISOString()),
  );

  const manifId = idManif || (manifestations?.[0] ? String(manifestations[0].idManif) : '');
  const lieuId = idLieu || (lieux?.[0] ? String(lieux[0].idLieu) : '');

  return (
    <div className="space-y-4">
      <Section title="Nouvelle séance">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Manifestation">
            <Select aria-label="Manifestation" value={manifId} onChange={(e) => setIdManif(e.target.value)}>
              {(manifestations ?? []).map((manif) => (
                <option key={manif.idManif} value={manif.idManif}>
                  {manif.libelle}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Lieu">
            <Select aria-label="Lieu" value={lieuId} onChange={(e) => setIdLieu(e.target.value)}>
              {(lieux ?? []).map((lieu) => (
                <option key={lieu.idLieu} value={lieu.idLieu}>
                  {lieu.libelle} ({lieu.timezone})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Début">
            <Input
              type="datetime-local"
              aria-label="Début de séance"
              value={dateSeance}
              onChange={(e) => setDateSeance(e.target.value)}
            />
          </Field>
          <Field label="Fin" hint="Obligatoire : sinon la commande devient invisible dans l’app">
            <Input
              type="datetime-local"
              aria-label="Fin de séance"
              value={dateFinSeance}
              onChange={(e) => setDateFinSeance(e.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            size="sm"
            className="mb-1"
            disabled={create.isPending || !manifId || !lieuId}
            onClick={() =>
              create.mutate(
                {
                  idManif: Number(manifId),
                  idLieu: Number(lieuId),
                  dateSeance: fromLocalInput(dateSeance) ?? new Date().toISOString(),
                  dateFinSeance: fromLocalInput(dateFinSeance) ?? new Date().toISOString(),
                  dateInitialeSeance: null,
                },
                { onSuccess: () => toast.success('Séance créée'), onError: (err) => toast.error(err) },
              )
            }
          >
            Créer
          </Button>
        </div>
      </Section>

      <Section title="Séances">
        {isPending ? <Spinner /> : null}
        {!isPending && !data?.length ? <EmptyState>Aucune séance.</EmptyState> : null}
        {data?.length ? (
          <table className={TABLE}>
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>idSeance</th>
                <th className={TH}>Manifestation</th>
                <th className={TH}>Lieu</th>
                <th className={TH}>Début</th>
                <th className={TH}>Fin</th>
                <th className={TH}>Date initiale</th>
                <th className={TH} />
              </tr>
            </thead>
            <tbody>
              {data.map((seance) => (
                <tr key={seance.idSeance} className={ROW}>
                  <td className={`${TD} font-mono text-xs text-outline`}>{seance.idSeance}</td>
                  <td className={TD}>
                    {manifestations?.find((m) => m.idManif === seance.idManif)?.libelle ?? seance.idManif}
                  </td>
                  <td className={TD}>{lieux?.find((l) => l.idLieu === seance.idLieu)?.libelle ?? seance.idLieu}</td>
                  <td className={`${TD} whitespace-nowrap`}>{formatDate(seance.dateSeance)}</td>
                  <td className={`${TD} whitespace-nowrap`}>{formatDate(seance.dateFinSeance)}</td>
                  <td className={`${TD} whitespace-nowrap`}>{formatDate(seance.dateInitialeSeance)}</td>
                  <td className={TD}>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Supprimer la séance ${seance.idSeance}`}
                      onClick={() =>
                        remove.mutate(seance.idSeance, {
                          onSuccess: () => toast.success('Séance supprimée'),
                          onError: (err) => toast.error(err),
                        })
                      }
                    >
                      <Trash2 size={15} aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Section>
    </div>
  );
}
